/**
 * Payroll's side of merit (remediation plan §5I step 9, §16, decision D2/Q8) — TWO-REVIEWER AREA.
 *
 * An approved `MeritCompensationProposal` (merit.contract.ts) changes nobody's pay by itself. A
 * payroll administrator (`compensation:update`) works the "Pending merit revisions" queue and applies
 * each proposal. Applying writes ONE new effective-dated `Compensation` row through the SAME internal
 * function a manual revision uses (payroll-compensation.ts `reviseCompensationTx`, under the same
 * `compensation:<employmentId>` lock), on the proposal's own pay basis, from the matrix effective
 * date; the previous row is closed the day before, never overwritten (invariant 5). The proposal
 * becomes `consumed` and remembers the row it wrote (unique), so a retried or repeated apply cannot
 * write a second row.
 *
 * BACK-DATING (Q8). The effective date may be in the past, back to the start of the current financial
 * year of the legal entity (422 before it — keeps TDS in one tax year). Months already paid at the old
 * rate by `finalized`/`released` runs get ONE `arrears` payroll input on the next open run of the
 * entity: Σ over those runs of (new − old) monthly on the row's basis (a CTC row through the payroll
 * engine's CTC breakdown). No open run → 409, nothing written; apply again once the next run is open.
 *
 * REFUSALS (409, with a `code` in the details): `later_revision_exists`, `employment_inactive`,
 * `basis_changed` / `compensation_changed` (the row in force is not the one the increment was worked
 * out on), `payroll_run_in_progress` (a run past its inputs would pay the old rate with no arrears),
 * `no_open_payroll_run`, `currency_mismatch`. Self: nobody applies their own increase (403).
 *
 * THE MODULE SWITCH (plan §27). Both routes answer 404 `merit_apply_disabled` while the
 * organisation's `MeritPayrollSettings.enabled` is off (default for every org; see
 * `payroll-merit-settings.contract.ts`, the switch's own router). NET-BASIS ARREARS MODE (plan §28,
 * decision P1): when arrears are due on a `net`-basis employment and the organisation's
 * `netBasisArrearsMode` is `block`, applying refuses with 409 `net_basis_arrears_blocked` instead of
 * paying the shortfall as a gross earning (the `accept_shortfall` default, unchanged).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  moneySchema,
  percentSchema,
  uuidSchema,
} from '../schemas/common.js';
import { payBasisSchema } from '../schemas/pay-basis.js';
import { meritCompensationProposalSchema } from './merit.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

/** Why a proposal cannot be applied as things stand — the same checks `applyMeritProposal` makes. */
export const MERIT_APPLY_BLOCKERS = [
  'own_record',
  'employment_inactive',
  'before_financial_year',
  'later_revision_exists',
  'basis_changed',
  'compensation_changed',
  'currency_mismatch',
  'no_ctc_template',
  'payroll_run_in_progress',
  'no_open_payroll_run',
  /** §28 P1: the organisation's `netBasisArrearsMode` is `block`. */
  'net_basis_arrears_blocked',
] as const;
export const meritApplyBlockerSchema = z.enum(MERIT_APPLY_BLOCKERS);
export type MeritApplyBlocker = z.infer<typeof meritApplyBlockerSchema>;

export const pendingMeritRevisionSchema = z.object({
  proposalId: uuidSchema,
  cycleId: uuidSchema,
  cycleLabel: z.string(),
  personId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string().nullable(),
  employmentId: uuidSchema,
  legalEntityId: uuidSchema.nullable(),
  effectiveDate: dateOnlySchema,
  payBasis: payBasisSchema,
  /** The amount in force on the effective date, on `payBasis`; null when none is. */
  currentCompensation: moneySchema.nullable(),
  proposedCompensation: moneySchema,
  incrementPercent: percentSchema,
  approvedAt: instantSchema,
  /** Months already paid at the old rate by final runs, and the arrears they would add (Q8). */
  arrearsMonths: z.number().int().nonnegative(),
  estimatedArrears: moneySchema.nullable(),
  /** Null when applying now would succeed. */
  blocker: meritApplyBlockerSchema.nullable(),
});
export type PendingMeritRevision = z.infer<typeof pendingMeritRevisionSchema>;

export const listPendingMeritRevisionsQuerySchema = z.object({
  legalEntityId: uuidSchema.optional(),
});

export const applyMeritProposalResponseSchema = z.object({
  proposal: meritCompensationProposalSchema,
  compensationId: uuidSchema,
  /** The arrears input created on the next open run, or null when no paid month needed one. */
  arrears: z
    .object({
      inputId: uuidSchema,
      runId: uuidSchema,
      months: z.number().int().positive(),
      amount: moneySchema,
    })
    .nullable(),
});
export type ApplyMeritProposalResponse = z.infer<typeof applyMeritProposalResponseSchema>;

export const payrollMeritContract = c.router(
  {
    listPendingMeritRevisions: {
      method: 'GET',
      path: '/pending',
      query: listPendingMeritRevisionsQuerySchema,
      responses: {
        200: z.object({ items: z.array(pendingMeritRevisionSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        /** `merit_apply_disabled` — the organisation's switch (plan §27) is off. */
        404: errorBodySchema,
      },
      summary:
        "Approved merit proposals not yet applied, for the employees whose compensation the caller may update, each with what would block applying it now and the arrears it would add — compensation:update",
      // Salary-bearing (current and new pay): never pulled into a copilot prompt (plan P1.1, R3).
      metadata: routeMeta('compensation:update', { ai: 'hidden', salaryPin: 'others' }),
    },

    applyMeritProposal: {
      method: 'POST',
      path: '/:proposalId/apply',
      headers: idempotencyHeadersSchema,
      body: z.object({}),
      responses: {
        201: applyMeritProposalResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Proposal not found, or `merit_apply_disabled` — the organisation's switch (§27) is off. */
        404: errorBodySchema,
        /** Not approved / already consumed or cancelled, or one of the 409 blockers (`code`). */
        409: errorBodySchema,
        /** Effective date before the current financial year; no CTC template on a ctc row. */
        422: errorBodySchema,
      },
      summary:
        'Apply an approved merit proposal: one new effective-dated Compensation row on the same basis from the matrix effective date (the previous row closed the day before), plus one arrears input on the next open run for months already paid — compensation:update. Requires Idempotency-Key',
      metadata: routeMeta(['compensation:update', 'payroll:read'], { ai: 'hidden', salaryPin: 'others' }),
    },
  },
  { pathPrefix: '/payroll/merit-revisions' },
);
export type PayrollMeritContract = typeof payrollMeritContract;
